import * as THREE from 'three';
import { isMobile } from 'react-device-detect';
import HotspotsFadeInOutEffect from '../Hotspot/HotspotsFadeInOutEffect';

import {
	SCROLL_CAMERA_DEFAULT_NAME,
	SCROLL_CAMERA_DEFAULT_DESKTOP_NAME,
	SCROLL_CAMERA_DEFAULT_MOBILE_NAME,
	SCROLL_SPEED,
	MOBILE_SPEED_MULTIPLIER,
} from './constants.js';

export default class ScrollToAnimateControls {
	constructor(
		mixer,
		renderer,
		model,
		animation,
		originalCamera,
		setCurrentCamera,
		scene,
		onRailsConfig = {},
	) {
		this.mixer = mixer;
		this.originalCamera = originalCamera;
		this.renderer = renderer;
		this.model = model;
		this.animation = animation;
		this.setCurrentCamera = setCurrentCamera;
		this.scene = scene;

		this.onRailsConfig = {
			scroll_camera_mobile_name: SCROLL_CAMERA_DEFAULT_MOBILE_NAME,
			scroll_camera_desktop_name: SCROLL_CAMERA_DEFAULT_DESKTOP_NAME,
			scroll_speed: SCROLL_SPEED,
			mobile_speed_multiplier: MOBILE_SPEED_MULTIPLIER,
			...onRailsConfig,
		};

		this._findAnimationCamera();
		if (!this.animationCamera) {
    		throw new Error('ScrollToAnimateControls: No suitable animation camera found in model');
		}

		this.originalCamera.fov = this.animationCamera?.fov;
		this.originalCamera.updateProjectionMatrix();
		this.controller = window.containerInstance_controller;
		this.v = new THREE.Vector3();
		this.hotspotsFadeInOutEffect = new HotspotsFadeInOutEffect();

		// set the main anim loop camera to be the camera that came with the glb
		if (isMobile) {
			this._setupMobileCamera();
		}

		this._setupAnimationVariables();

		// Touch tracking variables
		this.touchStartY = 0;
		this.lastTouchY = 0;
		this.isTouching = false;

		this._setupAnimationControls();
		this._setupAnimationLoop();

		this.clock = new THREE.Clock();
	}

	// Find the camera in the model that matches the scroll animation camera name
	_findAnimationCamera = () => {
		if (!this.model || !this.model.cameras) {
			console.warn('No cameras found in the model');
			return;
		}
		const scrollCameraName = isMobile
			? this.onRailsConfig.scroll_camera_mobile_name
			: this.onRailsConfig.scroll_camera_desktop_name;

		this.animationCamera =
			this.model.cameras.find(
				(camera) => camera?.name === scrollCameraName,
			) ??
			this.model.cameras.find(
				(camera) => camera?.name === SCROLL_CAMERA_DEFAULT_NAME,
			);
	};

	_setupAnimationVariables = () => {
		// Create an AnimationAction but don't play it automatically
		this.animationAction = this.mixer.clipAction(this.animation);
		this.animationAction.clampWhenFinished = true;
		this.animationAction.paused = true;
		this.animationAction.play(); // This puts it in the mixer but in paused state

		this.animationTime = 0;
		this.targetAnimationTime = 0; // Target time for easing
		this.animationDuration = this.animation ? this.animation.duration : 0;
		this.easingFactor = 0.1; // Controls smoothness (0.05-0.15 is a good range)
	};

	_setupMobileCamera = () => {
    if (!this.renderer || !this.animationCamera || !this.setCurrentCamera) {
        console.error('[ScrollToAnimateControls] Missing prerequisites for mobile camera setup');
        return;
    }

    try {
        const rendererSize = new THREE.Vector2();
        this.renderer.getSize(rendererSize);

        if (rendererSize.y > 0) {
            this.animationCamera.aspect = rendererSize.x / rendererSize.y;
            this.animationCamera.updateProjectionMatrix();
            this.setCurrentCamera(this.animationCamera);
        } else {
            console.warn('[ScrollToAnimateControls] Invalid renderer dimensions');
        }
    } catch (error) {
        console.error('[ScrollToAnimateControls] Mobile camera setup failed:', error);
    }
};

	timeToProgress(t) {
		if (t <= 0) return 0; // Start of animation

		if (t >= this.animation.duration) return 100; // End of animation
		return (t / this.animation.duration) * 100;
	}

	progressToTime(p) {
		if (p <= 0) return 0;
		if (p >= 100) return this.animation.duration;

		return (p / 100) * this.animation.duration;
	}

	jumpToAnimationProgress = (e) => {
		const progress = e.detail.progress;
		this.targetAnimationTime = this.progressToTime(progress);
	};

	dispose = () => {
		const element = this?.renderer?.domElement;
		if (element) {
			element.removeEventListener('wheel', this.handleAnimationScroll);

			element.removeEventListener('touchstart', this.handleTouchStart);
			element.removeEventListener('touchmove', this.handleTouchMove);
			element.removeEventListener('touchend', this.handleTouchEnd);
			window.removeEventListener(
				'jumpToAnimationProgress',
				this.jumpToAnimationProgress,
			);
			 document.removeEventListener(
            'seekAnimationTime',
            this.seekAnimationTime,
        );
		}
		this.scene.removeAnimationHook('ScrollToAnimateControls');

		// render again from the original camera
		this.setCurrentCamera(this.originalCamera);
	};

	_setupAnimationControls = () => {
		const element = this?.renderer?.domElement;
		if (element) {
			// Desktop scroll
			element.addEventListener('wheel', this.handleAnimationScroll, {
				passive: false,
			});

			// Mobile touch events
			element.addEventListener('touchstart', this.handleTouchStart, {
				passive: false,
			});
			element.addEventListener('touchmove', this.handleTouchMove, {
				passive: false,
			});
			element.addEventListener('touchend', this.handleTouchEnd, {
				passive: false,
			});
			window.addEventListener(
				'jumpToAnimationProgress',
				this.jumpToAnimationProgress,
				{
					passive: false,
				},
			);

			document.addEventListener(
				'seekAnimationTime',
				this.seekAnimationTime,
				{
					passive: false,
				},
			);
		}
	};

	_setupAnimationLoop = () => {
		this.scene.addAnimationHook('ScrollToAnimateControls', this.animate);
	};

	animate = () => {
		this.updateAnimationTime();
		this.animationCamera?.getWorldPosition(this.v);
		this.controller.moveTo(this.v.x, this.v.y, this.v.z, true);

		this.hotspotsFadeInOutEffect.tick(this.originalCamera);
	};

	rotateCameraToAnimationTrack = () => {
		const sourceCamera = this.animationCamera;

		const position = new THREE.Vector3();

		// compute the “look‐at” target by casting a ray forward
		const direction = new THREE.Vector3();
		sourceCamera.getWorldDirection(direction).multiplyScalar(0.1);
		const target = position.clone().add(direction);

		this.controller.setLookAt(
			position.x,
			position.y,
			position.z,
			target.x,
			target.y,
			target.z,
			true,
		);
	};
	seekAnimationTime = (event) => {
		if (
			!this.animationAction ||
			!this.animation ||
			event?.detail?.time === undefined
		)
			return;

		this.targetAnimationTime = event.detail.time;
	};

	updateAnimationTime = () => {
		if (!this.animationAction || !this.animation) return;

		// Smoothly interpolate current animation time toward target time
		this.animationTime +=
			(this.targetAnimationTime - this.animationTime) * this.easingFactor;
		this.animationAction.time = this.animationTime;
		if (Math.abs(this.targetAnimationTime - this.animationTime) > 0.001) {
			this.rotateCameraToAnimationTrack();
		}

		let progress = this.timeToProgress(this.animationTime);
		let ev = new CustomEvent('setAnimationProgress', {
			detail: { progress: progress },
		});
		window.dispatchEvent(ev);
	};

	// Step forward in the animation
	stepForward = (amount = this.onRailsConfig.scroll_speed) => {
		if (!this.animationAction || !this.animation) return;

		// Update the target time, not the actual time
		this.targetAnimationTime = Math.min(
			this.targetAnimationTime + amount,
			this.animationDuration,
		);

		// If user keeps scrolling down, then restart animation
		if (this.targetAnimationTime === this.animationDuration) {
			this.resetScroll('forward');
		}
	};

	// Reset if reached upper/lower end
	resetScroll = (direction) => {
		if (direction == 'forward') {
			this.targetAnimationTime = 0;
			this.animationTime = 0;
		} else {
			this.targetAnimationTime = this.animationDuration;
			this.animationTime = this.animationDuration;
		}
		this.animationAction.reset();
		this.animationAction.paused = true;
	};

	// Step backward in the animation
	stepBackward = (amount = this.onRailsConfig.scroll_speed) => {
		if (!this.animationAction || !this.animation) return;

		// Update the target time, not the actual time
		this.targetAnimationTime = Math.max(
			this.targetAnimationTime - amount,
			0,
		);

		// If user keeps scrolling up, then restart animation
		if (this.targetAnimationTime === 0) {
			this.resetScroll('backward');
		}
	};

	handleAnimationScroll = (event) => {
		event.preventDefault();
		// negative deltaY (scrolling down) moves animation forward
		// positive deltaY (scrolling up) moves animation backward
		event.deltaY > 0 ? this.stepForward() : this.stepBackward();
	};

	// Touch event handlers for mobile support
	handleTouchStart = (event) => {
		event.preventDefault();
		this.isTouching = true;
		this.touchStartY = event.touches[0].clientY;
		this.lastTouchY = this.touchStartY;
	};

	handleTouchMove = (event) => {
		if (!this.isTouching) return;
		event.preventDefault();

		const currentY = event.touches[0].clientY;
		const deltaY = currentY - this.lastTouchY;

		// Use the same logic as scroll:
		// swiping down moves animation forward
		// swiping up moves animation backward
		// but the gesture is inverted vs. scroll wheel
		const mobileScrollSpeed =
			this.onRailsConfig.mobile_speed_multiplier *
			this.onRailsConfig.scroll_speed;
		if (Math.abs(deltaY) > 2) {
			// Small threshold to avoid tiny movements
			const stepSize = Math.abs(deltaY) * mobileScrollSpeed;
			deltaY > 0
				? this.stepBackward(stepSize)
				: this.stepForward(stepSize);
			this.lastTouchY = currentY;
		}
	};

	handleTouchEnd = (event) => {
		event.preventDefault();
		this.isTouching = false;
	};
}
